import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Eye, EyeSlash, Trash, Plus, Key, ToggleLeft, ToggleRight, X } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

/* Two sibling panels that live under the Storyboard → Brand kit area.
   Exposed individually so the parent can tab between them. */

export function ModelsRegistryPanel() {
  const [reg, setReg] = useState(null);
  const [adding, setAdding] = useState(null);  // null | "script" | "image" | "video" | "voice"
  const load = useCallback(() => {
    api.get("/admin/social/models-registry").then((r) => setReg(r.data)).catch((e) =>
      toast.error(formatApiErrorDetail(e.response?.data?.detail))
    );
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id, patch) => {
    try {
      await api.put(`/admin/social/models-registry/${id}`, patch);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (id, label) => {
    if (!window.confirm(`Delete "${label}" from the registry?`)) return;
    try {
      await api.delete(`/admin/social/models-registry/${id}`);
      toast.success("Model removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Iter-39 — On-demand re-probe of every enabled script-step model.
  const [probing, setProbing] = useState(false);
  const probeAll = async () => {
    setProbing(true);
    try {
      const { data } = await api.post("/admin/social/models-registry/probe");
      toast.success(
        `Probe done: ${data.ok} ok · ${data.disabled} disabled · ${data.transient} transient`,
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Probe failed");
    } finally {
      setProbing(false);
    }
  };

  if (!reg) return <div className="text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="models-registry-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Toggle which models surface in the Storyboard composer wizard. Disabled models stay
          in the catalog but never appear in the pickers. The cost field flows into the budget
          previews and recommendation engine. <b>Add custom model</b> registers any reliable
          model you want — provider's exact <code>model_id</code> is what gets passed to the SDK.
        </p>
        <button
          onClick={probeAll}
          disabled={probing}
          className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="registry-probe-all"
          title="Send a 1-token probe to each enabled script model — disables any that the provider rejects so you don't burn credits."
        >
          {probing ? "Probing…" : "Probe all (1-token health check)"}
        </button>
      </div>
      {["script", "image", "video", "voice"].map((step) => (
        <div key={step} className="card-flat p-4" data-testid={`registry-step-${step}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow capitalize">{step} models</div>
            <button
              onClick={() => setAdding(step)}
              className="btn-ghost px-2.5 py-1 text-[11px] inline-flex items-center gap-1"
              data-testid={`registry-add-${step}`}
            >
              <Plus size={11} weight="bold" /> Add custom
            </button>
          </div>
          <div className="space-y-2">
            {(reg[step] || []).map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 py-2 border-b hairline last:border-b-0">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">
                    {m.label}
                    {m.badge && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--secondary))]/15 text-[hsl(var(--secondary))]">
                        {m.badge}
                      </span>
                    )}
                    {m.default && <span className="ml-2 text-[10px] text-[hsl(var(--primary))]">DEFAULT</span>}
                    {/* Iter-39 — probe status pill */}
                    <ProbeStatusPill status={m.last_probe_status} error={m.last_probe_error} at={m.last_probed_at} />
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {m.provider} · {m.model_id}
                    {m.requires_provider_key && (
                      <span className="ml-2 text-amber-600">needs {m.requires_provider_key} key</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-[10px] text-muted-foreground">
                    ₹{" "}
                    <input
                      type="number" min={0} step="0.5"
                      defaultValue={m.cost_per_unit_inr}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        if (v !== m.cost_per_unit_inr) toggle(m.id, { cost_per_unit_inr: v });
                      }}
                      className="bg-background border hairline px-1.5 py-0.5 text-xs w-16"
                      data-testid={`registry-cost-${m.model_id}`}
                    />/{m.unit}
                  </label>
                  {!m.default && (
                    <button onClick={() => toggle(m.id, { default: true })} className="text-[10px] text-muted-foreground hover:text-[hsl(var(--primary))]">
                      Set default
                    </button>
                  )}
                  <button
                    onClick={() => toggle(m.id, { enabled: !m.enabled })}
                    className="inline-flex items-center"
                    data-testid={`registry-toggle-${m.model_id}`}
                    aria-label={m.enabled ? "Disable" : "Enable"}
                  >
                    {m.enabled
                      ? <ToggleRight size={26} weight="fill" className="text-[hsl(var(--primary))]" />
                      : <ToggleLeft size={26} weight="duotone" className="text-muted-foreground" />}
                  </button>
                  <button
                    onClick={() => remove(m.id, m.label)}
                    className="text-muted-foreground hover:text-destructive opacity-60 hover:opacity-100"
                    data-testid={`registry-delete-${m.model_id}`}
                    aria-label="Delete"
                  >
                    <Trash size={14} weight="duotone" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {adding && (
        <AddCustomModelModal
          step={adding}
          onClose={() => setAdding(null)}
          onCreated={() => { setAdding(null); load(); }}
        />
      )}
    </div>
  );
}

const CURATED_MODELS = {
  script: [
    { provider: "anthropic", model_id: "claude-opus-4-5",          label: "Claude Opus 4.5",            cost: 35, badge: "quality" },
    { provider: "anthropic", model_id: "claude-haiku-4-5",         label: "Claude Haiku 4.5",           cost: 4,  badge: "fast" },
    { provider: "openai",    model_id: "gpt-5.2-mini",             label: "GPT-5.2 mini",                cost: 8,  badge: "economical" },
    { provider: "gemini",    model_id: "gemini-3-flash",           label: "Gemini 3 Flash",             cost: 5,  badge: "fast" },
  ],
  image: [
    { provider: "openai",    model_id: "gpt-image-1-hd",            label: "GPT Image 1 HD",             cost: 30, badge: "quality" },
    { provider: "fal-ai",    model_id: "fal-ai/flux-pro-1.1",      label: "FLUX 1.1 Pro (fal.ai)",      cost: 18, badge: "quality", requires_key: "fal" },
    { provider: "fal-ai",    model_id: "fal-ai/flux-schnell",      label: "FLUX Schnell (fal.ai)",      cost: 3,  badge: "fast",    requires_key: "fal" },
  ],
  video: [
    { provider: "heygen",    model_id: "heygen-avatar-v1",         label: "HeyGen avatar v1",           cost: 45, badge: "fast",    requires_key: "heygen" },
    { provider: "fal-ai",    model_id: "fal-ai/kling-1.5",         label: "Kling 1.5 (fal.ai)",         cost: 35, badge: "economical", requires_key: "fal" },
    { provider: "openai",    model_id: "sora-2-pro",                label: "Sora 2 Pro (1080p)",         cost: 120, badge: "quality" },
  ],
  voice: [
    { provider: "elevenlabs", model_id: "eleven-flash-v2.5",        label: "ElevenLabs Flash v2.5",      cost: 4,  badge: "fast",    requires_key: "elevenlabs" },
    { provider: "elevenlabs", model_id: "eleven-turbo-v2.5",        label: "ElevenLabs Turbo v2.5",      cost: 5,  badge: "fast",    requires_key: "elevenlabs" },
    { provider: "openai",     model_id: "tts-1-hd",                 label: "OpenAI TTS-1 HD",            cost: 6,  badge: "quality" },
  ],
};

function AddCustomModelModal({ step, onClose, onCreated }) {
  const curated = CURATED_MODELS[step] || [];
  const [form, setForm] = useState({
    step, provider: "", model_id: "", label: "",
    cost_per_unit_inr: step === "script" ? 12 : step === "image" ? 8 : step === "video" ? 60 : 5,
    unit: step === "script" ? "storyboard" : "scene",
    enabled: true, default: false, requires_provider_key: "", badge: "",
  });
  const [busy, setBusy] = useState(false);
  const applyCurated = (c) => setForm({
    ...form,
    provider: c.provider, model_id: c.model_id, label: c.label,
    cost_per_unit_inr: c.cost, badge: c.badge,
    requires_provider_key: c.requires_key || "",
  });
  const submit = async () => {
    if (!form.provider.trim() || !form.model_id.trim() || !form.label.trim()) {
      toast.error("Provider, model_id and label are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/social/models-registry", {
        ...form,
        requires_provider_key: form.requires_provider_key.trim() || null,
        badge: form.badge.trim() || null,
      });
      toast.success(`Added ${form.label} to ${form.step}`);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-background border hairline max-w-xl w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="registry-add-modal">
        <div className="flex items-center justify-between">
          <div className="eyebrow capitalize">Add {step} model</div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={16} weight="bold" /></button>
        </div>

        {curated.length > 0 && (
          <div data-testid="registry-add-curated">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
              Quick pick — curated reliable models
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {curated.map((c) => (
                <button
                  key={c.model_id}
                  onClick={() => applyCurated(c)}
                  className="text-left card-flat p-2 hover:border-[hsl(var(--primary))] border-l-2 border-transparent transition-colors"
                  data-testid={`registry-curated-${c.model_id}`}
                >
                  <div className="text-sm font-medium">{c.label}</div>
                  <div className="text-[10px] text-muted-foreground">{c.provider} · ₹{c.cost} · {c.badge}{c.requires_key ? ` · needs ${c.requires_key} key` : ""}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <RegField label="Provider *" value={form.provider} onChange={(v) => setForm({ ...form, provider: v })} placeholder="anthropic / openai / gemini / fal-ai" testid="reg-provider" />
        <RegField label="model_id *" value={form.model_id} onChange={(v) => setForm({ ...form, model_id: v })} placeholder="exact id passed to the SDK" testid="reg-model-id" />
        <RegField label="Label *" value={form.label} onChange={(v) => setForm({ ...form, label: v })} placeholder="Friendly label for the picker" testid="reg-label" />
        <div className="grid grid-cols-3 gap-2">
          <RegField label="₹ per unit" type="number" value={form.cost_per_unit_inr} onChange={(v) => setForm({ ...form, cost_per_unit_inr: parseFloat(v) || 0 })} testid="reg-cost" />
          <RegField label="Unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} placeholder="scene / storyboard" testid="reg-unit" />
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Badge</span>
            <select
              value={form.badge}
              onChange={(e) => setForm({ ...form, badge: e.target.value })}
              className="w-full bg-background border hairline px-2 py-1.5 text-sm"
              data-testid="reg-badge"
            >
              <option value="">(none)</option>
              <option value="fast">fast</option>
              <option value="quality">quality</option>
              <option value="economical">economical</option>
            </select>
          </label>
        </div>
        <RegField label="Requires provider key (slug)" value={form.requires_provider_key} onChange={(v) => setForm({ ...form, requires_provider_key: v })} placeholder="leave blank for Emergent Universal Key" testid="reg-needs-key" />
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" checked={form.default} onChange={(e) => setForm({ ...form, default: e.target.checked })} />
          Make default for the {step} step
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary px-4 py-1.5 text-xs disabled:opacity-50" data-testid="reg-add-save">
            {busy ? "Adding…" : "Add to registry"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RegField({ label, value, onChange, placeholder = "", type = "text", testid }) {
  return (
    <label className="text-sm block">
      <span className="block text-xs text-muted-foreground mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-background border hairline px-2 py-1.5 text-sm"
        data-testid={testid}
      />
    </label>
  );
}

export function ProviderKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [adding, setAdding] = useState({ provider: "", api_key: "", note: "" });
  const [revealed, setRevealed] = useState({});  // provider → full key
  const load = useCallback(() => {
    api.get("/admin/social/provider-keys").then((r) => setKeys(r.data)).catch(() => setKeys([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!adding.provider.trim() || !adding.api_key.trim()) {
      toast.error("Provider name + API key required");
      return;
    }
    try {
      await api.post("/admin/social/provider-keys", adding);
      toast.success(`Saved ${adding.provider} key`);
      setAdding({ provider: "", api_key: "", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const reveal = async (provider) => {
    if (revealed[provider]) {
      setRevealed((r) => { const n = { ...r }; delete n[provider]; return n; });
      return;
    }
    if (!window.confirm(`Reveal the ${provider} API key? This is audit-logged.`)) return;
    try {
      const { data } = await api.post(`/admin/social/provider-keys/${provider}/reveal`);
      setRevealed((r) => ({ ...r, [provider]: data.api_key }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (provider) => {
    if (!window.confirm(`Delete the ${provider} key permanently?`)) return;
    try {
      await api.delete(`/admin/social/provider-keys/${provider}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="provider-keys-panel">
      <p className="text-xs text-muted-foreground max-w-2xl">
        Paste API keys for non-Emergent integrations (HeyGen, ElevenLabs, direct OpenAI keys,
        etc). Keys are stored server-side and shown masked on this page —{" "}
        <b>reveal-once</b> emits an admin audit row, just like the Data Viewer.
      </p>

      <div className="card-flat p-4" data-testid="provider-key-add-form">
        <div className="eyebrow mb-3 inline-flex items-center gap-1.5">
          <Plus size={14} weight="duotone" /> Add / update key
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <input
            placeholder="provider (e.g. elevenlabs)"
            value={adding.provider}
            onChange={(e) => setAdding({ ...adding, provider: e.target.value })}
            className="bg-background border hairline px-2 py-1.5 text-sm"
            data-testid="provider-key-input-provider"
          />
          <input
            placeholder="API key"
            value={adding.api_key}
            onChange={(e) => setAdding({ ...adding, api_key: e.target.value })}
            className="bg-background border hairline px-2 py-1.5 text-sm font-mono"
            data-testid="provider-key-input-key"
          />
          <input
            placeholder="note (optional)"
            value={adding.note}
            onChange={(e) => setAdding({ ...adding, note: e.target.value })}
            className="bg-background border hairline px-2 py-1.5 text-sm"
            data-testid="provider-key-input-note"
          />
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={save} className="btn-primary px-4 py-1.5 text-xs" data-testid="provider-key-save">
            Save key
          </button>
        </div>
      </div>

      {keys.length === 0 ? (
        <div className="card-flat p-6 text-center text-xs text-muted-foreground" data-testid="provider-keys-empty">
          <Key size={24} weight="duotone" className="mx-auto mb-2 opacity-50" />
          No provider keys yet. Add one above to unlock HeyGen / ElevenLabs / direct OpenAI models in the wizard.
        </div>
      ) : (
        <div className="space-y-2" data-testid="provider-keys-list">
          {keys.map((k) => (
            <div key={k.id} className="card-flat p-3 flex items-center gap-3" data-testid={`provider-key-${k.provider}`}>
              <Key size={18} weight="duotone" className="text-[hsl(var(--secondary))]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{k.provider}</div>
                <div className="text-[11px] font-mono text-muted-foreground truncate">
                  {revealed[k.provider] || k.masked}
                </div>
                {k.note && <div className="text-[10px] text-muted-foreground mt-0.5">{k.note}</div>}
              </div>
              <button
                onClick={() => reveal(k.provider)}
                className="btn-ghost px-2 py-1 text-[11px] inline-flex items-center gap-1"
                data-testid={`provider-key-reveal-${k.provider}`}
              >
                {revealed[k.provider] ? <EyeSlash size={12} weight="duotone" /> : <Eye size={12} weight="duotone" />}
                {revealed[k.provider] ? "Hide" : "Reveal"}
              </button>
              <button
                onClick={() => remove(k.provider)}
                className="btn-ghost px-2 py-1 text-[11px] text-muted-foreground inline-flex items-center gap-1"
                data-testid={`provider-key-delete-${k.provider}`}
              >
                <Trash size={12} weight="duotone" /> Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Iter-39 — probe status pill for the Models Registry rows
function ProbeStatusPill({ status, error, at }) {
  if (!status) return null;
  const tone = status === "ok"
    ? { label: "Healthy",   pill: "bg-[#5FB37C]/15 text-[#5FB37C]" }
    : status === "disabled"
    ? { label: "Rejected",  pill: "bg-[#D2493A]/15 text-[#D2493A]" }
    : status === "transient"
    ? { label: "Transient", pill: "bg-[#C88A2E]/15 text-[#C88A2E]" }
    : { label: status,      pill: "bg-muted text-muted-foreground" };
  const title = [
    `Last probe: ${status}`,
    at ? `at ${new Date(at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : null,
    error ? `· ${error.slice(0, 160)}` : null,
  ].filter(Boolean).join(" ");
  return (
    <span
      title={title}
      className={`ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${tone.pill}`}
      data-testid={`probe-status-${status}`}
    >
      {tone.label}
    </span>
  );
}

